import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import IntakeFlowPanel from './IntakeFlowPanel';
import UsageActivityPanel from './UsageActivityPanel';
import ValidationHealthPanel from './ValidationHealthPanel';
import FeedbackList from './FeedbackList';

const AdminSettings = () => {
  const { authenticatedFetch } = useAuth();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/admin/demo/waba-status`
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to access admin settings');
        }
        throw new Error('Failed to load demo WABA status');
      }

      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Error loading demo WABA status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, API_URL]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleFacebookLogin = async (isRefresh = false) => {
    setConnecting(true);
    setError(null);
    setSuccess(null);

    if (!window.FB) {
      setError('Facebook SDK not loaded. Please refresh the page and try again.');
      setConnecting(false);
      return;
    }

    window.FB.login(
      (response) => {
        (async () => {
          if (response.status === 'connected') {
            try {
              const token = response.authResponse.accessToken;
              console.log('Facebook login successful, sending token to backend...');

              const endpoint = isRefresh
                ? `${API_URL}/api/admin/demo/refresh-waba-token`
                : `${API_URL}/api/admin/demo/initialize-waba`;

              const apiResponse = await authenticatedFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: token })
              });

              if (!apiResponse.ok) {
                if (apiResponse.status === 403) {
                  throw new Error('You do not have permission to manage demo WABA');
                }
                const errorData = await apiResponse.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to update demo WABA');
              }

              const data = await apiResponse.json();
              setStatus(data);
              setSuccess(isRefresh
                ? 'Demo WABA token refreshed successfully!'
                : 'Demo WABA initialized successfully!');
            } catch (err) {
              console.error('Error updating demo WABA:', err);
              setError(err.message);
            }
          } else {
            setError('Facebook login failed or was cancelled');
          }
          setConnecting(false);
        })();
      },
      {
        scope: 'whatsapp_business_management,business_management,whatsapp_business_manage_events',
        auth_type: 'rerequest'
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpirationChip = () => {
    if (!status || !status.isConfigured) return null;

    if (status.isExpired) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Token Expired"
          color="error"
          size="small"
        />
      );
    }

    if (status.daysRemaining <= 7) {
      return (
        <Chip
          icon={<WarningIcon />}
          label={`Expires in ${status.daysRemaining} days`}
          color="warning"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<CheckCircleIcon />}
        label={`${status.daysRemaining} days remaining`}
        color="success"
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Demo WABA Administration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage the demo WhatsApp Business Account connection shared by all demo users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Current Status Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Current Status
        </Typography>

        {!status?.isConfigured ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Demo WABA connection not found. Click the button below to connect with Facebook.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                WABA Name
              </Typography>
              <Typography variant="body1">
                {status.wabaName || 'Unknown'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                WABA ID
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {status.wabaId || 'Unknown'}
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Token Expires
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {formatDate(status.tokenExpiresAt)}
                </Typography>
                {getExpirationChip()}
              </Box>
            </Box>
          </Stack>
        )}
      </Paper>

      {/* Initialize WABA Section - shown when not configured */}
      {!status?.isConfigured && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Initialize Demo WABA
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect with Facebook to authorize access to your WhatsApp Business Account.
            The token will be automatically exchanged for a long-lived token (~60 days).
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={connecting ? <CircularProgress size={20} color="inherit" /> : <FacebookIcon />}
            onClick={() => handleFacebookLogin(false)}
            disabled={connecting}
            sx={{
              bgcolor: '#1877F2',
              '&:hover': { bgcolor: '#166FE5' }
            }}
          >
            {connecting ? 'Connecting...' : 'Connect with Facebook'}
          </Button>
        </Paper>
      )}

      {/* Refresh Token Section - shown when configured */}
      {status?.isConfigured && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Refresh Access Token
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Re-authenticate with Facebook to get a new access token.
            The token will be automatically exchanged for a long-lived token (~60 days).
          </Typography>

          <Button
            variant="contained"
            startIcon={connecting ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={() => handleFacebookLogin(true)}
            disabled={connecting}
          >
            {connecting ? 'Refreshing...' : 'Refresh Token'}
          </Button>
        </Paper>
      )}

      {/* What people are asking the generator for, across every account. First because it is the
          one panel here you read to learn something rather than to fix something. */}
      <UsageActivityPanel />

      {/* Which flow the concierge sends when asked to build something. */}
      <IntakeFlowPanel />

      {/* Validation discrepancy corpus (ADR-0006) */}
      <ValidationHealthPanel />

      {/* User feedback / bug reports */}
      <FeedbackList />
    </Box>
  );
};

export default AdminSettings;
